// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/AjustePrecoRecebimento.tsx
//
//  Iteração 5 · Fase 2 — Ajuste de Preço de Venda pós-recebimento.
//  Grid editável com custo + impostos agregados + margem → preço sugerido;
//  usuário aceita ou ajusta. Ao aplicar, grava ProdutoPrecoHistorico.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { ArrowLeft, Calculator, CheckCircle2, TrendingUp, Info, Send } from 'lucide-react';

import {
  useAjustePrecoPreview,
  useAplicarAjustePreco,
} from '@/features/compras/hooks/useRecebimento';
import type { AjustePrecoLinha } from '@/features/compras/types';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmt = (v: number, d = 2) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });

interface LinhaEditavel {
  produtoId:      number;
  precoNovo:      number;
  margemEditada:  number; // usuário pode ajustar margem → recalcula preço
  custoUnit:      number;
}

const AjustePrecoRecebimento = () => {
  const { id } = useParams<{ id: string }>();
  const recebimentoId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: preview, isLoading, error } = useAjustePrecoPreview(recebimentoId);
  const { aplicar, isApplying } = useAplicarAjustePreco();

  const [edits, setEdits] = useState<Record<number, LinhaEditavel>>({});
  const [observacao, setObservacao] = useState('');

  // Inicializa edits quando preview chega
  useEffect(() => {
    if (!preview) return;
    const novo: Record<number, LinhaEditavel> = {};
    for (const l of preview.itens) {
      novo[l.numeroItem] = {
        produtoId:     l.produtoId,
        precoNovo:     l.precoSugerido,
        margemEditada: l.percentualMargemAplicada,
        custoUnit:     l.custoReposicaoUnitario,
      };
    }
    setEdits(novo);
  }, [preview]);

  const setPreco = (numeroItem: number, precoNovo: number) => {
    setEdits((prev) => {
      const atual = prev[numeroItem];
      if (!atual) return prev;
      const margem = atual.custoUnit > 0
        ? ((precoNovo - atual.custoUnit) / atual.custoUnit) * 100
        : 0;
      return { ...prev, [numeroItem]: { ...atual, precoNovo, margemEditada: margem } };
    });
  };

  const setMargem = (numeroItem: number, margem: number) => {
    setEdits((prev) => {
      const atual = prev[numeroItem];
      if (!atual) return prev;
      const precoNovo = Math.round(atual.custoUnit * (1 + margem / 100) * 100) / 100;
      return { ...prev, [numeroItem]: { ...atual, precoNovo, margemEditada: margem } };
    });
  };

  const linhasComAlteracao = useMemo(() => {
    if (!preview) return [] as AjustePrecoLinha[];
    return preview.itens.filter((l) => {
      const e = edits[l.numeroItem];
      return e && Math.abs(e.precoNovo - l.precoAtual) > 0.009;
    });
  }, [preview, edits]);

  const handleAplicar = async () => {
    if (!preview) return;
    if (linhasComAlteracao.length === 0) {
      toast.info('Nenhum preço foi alterado.');
      return;
    }
    try {
      const resultado = await aplicar({
        recebimentoId,
        request: {
          usuarioId: user?.id ?? 0,
          observacao: observacao || undefined,
          itens: linhasComAlteracao.map((l) => {
            const e = edits[l.numeroItem];
            return {
              produtoId:                  l.produtoId,
              precoNovo:                  e.precoNovo,
              custoReposicao:             l.custoReposicaoUnitario,
              percentualMargemAplicada:   e.margemEditada,
              percentualCustoOperacional: preview.config.percentualCustoOperacional,
            };
          }),
        },
      });
      toast.success(
        `${resultado.produtosAtualizados} produto${resultado.produtosAtualizados > 1 ? 's atualizados' : ' atualizado'} com sucesso.`,
      );
      navigate('/compras/recebimentos/importar');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  if (isLoading)
    return <div className="p-6 text-sm text-muted-foreground">Carregando preview…</div>;

  if (error || !preview)
    return (
      <div className="p-6 space-y-4">
        <div className="text-sm text-destructive">
          {error ? getApiErrorMessage(error) : 'Preview indisponível.'}
        </div>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Button>
      </div>
    );

  const cfg = preview.config;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
            </Button>
            <h1 className="text-3xl font-bold">
              Ajuste de Preço · Recebimento #{preview.recebimentoNumero}
            </h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Revise o preço de venda sugerido com base no custo de reposição de cada item.
            Toda alteração é registrada em log para auditoria.
          </p>
        </div>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => navigate(`/compras/recebimentos/${recebimentoId}/manifestacao`)}
        >
          <Send className="h-4 w-4" /> Manifestação DFe
        </Button>
      </div>

      {/* Cards com a config aplicada */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4 text-primary" />
            Parâmetros de cálculo (config da empresa)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 text-xs">
            {[
              ['ICMS compõe custo',    cfg.icmsCompeCusto],
              ['ICMS ST compõe custo', cfg.icmsStCompeCusto],
              ['IPI compõe custo',     cfg.ipiCompeCusto],
              ['PIS/COFINS compõe',    cfg.pisCofinsCompeCusto],
              ['Frete compõe custo',   cfg.freteCompeCusto],
              ['Seguro compõe custo',  cfg.seguroCompeCusto],
              ['Outros compõem',       cfg.outrosCompeCusto],
            ].map(([lbl, v]) => (
              <Badge
                key={lbl as string}
                variant={v ? 'default' : 'outline'}
                className={v ? '' : 'text-muted-foreground'}
              >
                {lbl as string}: {v ? 'Sim' : 'Não'}
              </Badge>
            ))}
            <Badge variant="secondary">
              % operacional: {fmt(cfg.percentualCustoOperacional)}%
            </Badge>
            <Badge variant="secondary">
              margem padrão: {fmt(cfg.percentualMargemPadrao)}%
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="pt-6">
          <div className="rounded-md border overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead className="text-right">Qtd</TableHead>
                  <TableHead className="text-right">Custo bruto</TableHead>
                  <TableHead className="text-right">Impostos agreg.</TableHead>
                  <TableHead className="text-right">Operacional</TableHead>
                  <TableHead className="text-right">Custo reposição un.</TableHead>
                  <TableHead className="text-right">Preço atual</TableHead>
                  <TableHead className="text-right">Margem (%)</TableHead>
                  <TableHead className="text-right">Preço novo</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.itens.map((l) => {
                  const e = edits[l.numeroItem];
                  const alterou = e && Math.abs(e.precoNovo - l.precoAtual) > 0.009;
                  const aumenta = e && e.precoNovo > l.precoAtual;
                  return (
                    <TableRow key={l.numeroItem}>
                      <TableCell className="text-xs">{l.numeroItem}</TableCell>
                      <TableCell>
                        <div className="max-w-[240px]">
                          <div className="text-sm font-medium truncate" title={l.descricaoProduto}>
                            {l.descricaoProduto}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Cód. {l.produtoId}
                            {l.gtin ? ` · GTIN ${l.gtin}` : ''}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-xs">{fmt(l.quantidade, 3)} {l.unidade}</TableCell>
                      <TableCell className="text-right text-xs">R$ {fmt(l.custoBrutoItem)}</TableCell>
                      <TableCell className="text-right text-xs">
                        <span className="font-medium">R$ {fmt(l.totalImpostosAgregados)}</span>
                        <div className="text-[10px] text-muted-foreground">
                          ICMS {fmt(l.icmsCompoeCusto)} · ST {fmt(l.icmsStCompoeCusto)} · IPI {fmt(l.ipiCompoeCusto)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        R$ {fmt(l.acrescimoOperacional)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        R$ {fmt(l.custoReposicaoUnitario, 4)}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        R$ {fmt(l.precoAtual)}
                        {l.precoAtual > 0 && (
                          <div className="text-[10px] text-muted-foreground">
                            margem atual: {fmt(l.margemAtualPercent)}%
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          className="w-20 text-right text-xs"
                          value={e?.margemEditada.toFixed(2) ?? ''}
                          onChange={(ev) => setMargem(l.numeroItem, Number(ev.target.value) || 0)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          className="w-28 text-right text-sm font-semibold"
                          value={e?.precoNovo.toFixed(2) ?? ''}
                          onChange={(ev) => setPreco(l.numeroItem, Number(ev.target.value) || 0)}
                        />
                      </TableCell>
                      <TableCell>
                        {!alterou ? (
                          <Badge variant="outline" className="text-xs">Sem alteração</Badge>
                        ) : aumenta ? (
                          <Badge className="text-xs gap-1 bg-green-600 hover:bg-green-600">
                            <TrendingUp className="h-3 w-3" /> aumenta
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">reduz</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Ação */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-1">
            <Label>Observação (opcional) — será incluída no log</Label>
            <Textarea
              rows={2}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Ex: ajuste de preço aplicado após reunião com gerente comercial"
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <div className="text-muted-foreground">
                {linhasComAlteracao.length} produto{linhasComAlteracao.length === 1 ? '' : 's'} com alteração
                {' · '}
                {preview.itens.length - linhasComAlteracao.length} sem mudança
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                Cancelar
              </Button>
              <Button
                onClick={handleAplicar}
                disabled={isApplying || linhasComAlteracao.length === 0}
                className="gap-2"
              >
                {isApplying ? (
                  <Calculator className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {isApplying ? 'Aplicando…' : 'Aplicar alterações'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AjustePrecoRecebimento;
